import {
  PUBLIC_CREDIT_COSTS,
  PUBLIC_PRICING_PLANS,
  PUBLIC_TOP_UP,
  PUBLIC_YEARLY_DISCOUNT_PERCENT,
  REFERRAL_CREDIT_AMOUNT,
  VECTORIZER_FREE_DAILY_IMAGES,
  VECTORIZER_PAID_IMAGE_CREDITS,
  WELCOME_CREDIT_AMOUNT,
  formatPublicCount,
  formatPublicUsd
} from './publicOfferFacts.js';

// Shared pieces for the per-locale offer copy in
// src/data/i18n/appRouteOfferContent.{lang}.js. Each locale module only holds
// wording; every number it prints comes from the context built here.
export const NBSP = ' ';

export const plural = (one, many) => (value) => `${value} ${Number(value) === 1 ? one : many}`;

export const buildOfferContext = (language) => {
  const usd = (value) => formatPublicUsd(value, language);
  const count = (value) => formatPublicCount(value, language);
  const plans = PUBLIC_PRICING_PLANS.map((plan) => ({
    name: plan.name,
    credits: count(plan.credits),
    monthly: usd(plan.monthlyPrice),
    yearly: plan.yearlyPrice === null ? null : usd(plan.yearlyPrice),
    yearlyPerMonth: plan.yearlyPricePerMonth === null ? null : usd(plan.yearlyPricePerMonth)
  }));
  const examplePlan = PUBLIC_PRICING_PLANS[1] || PUBLIC_PRICING_PLANS[0];
  const exampleCredits = examplePlan?.credits || 0;
  const costs = PUBLIC_CREDIT_COSTS;

  return {
    plans,
    first: plans[0],
    last: plans[plans.length - 1],
    planCount: plans.length,
    discount: PUBLIC_YEARLY_DISCOUNT_PERCENT,
    topUpCredits: count(PUBLIC_TOP_UP?.credits),
    topUpPrice: usd(PUBLIC_TOP_UP?.price),
    fast: costs.fastImage,
    standard: costs.standardImage,
    deep: costs.deepImage,
    videoSeconds: costs.videoWindowSeconds,
    maxVideoSeconds: costs.maxVideoSeconds,
    welcome: count(WELCOME_CREDIT_AMOUNT),
    welcomeImages: count(Math.floor(WELCOME_CREDIT_AMOUNT / costs.fastImage)),
    referral: count(REFERRAL_CREDIT_AMOUNT),
    vectorFree: count(VECTORIZER_FREE_DAILY_IMAGES),
    vectorPaid: VECTORIZER_PAID_IMAGE_CREDITS,
    example: {
      name: examplePlan?.name,
      credits: count(exampleCredits),
      fast: count(Math.floor(exampleCredits / costs.fastImage)),
      standard: count(Math.floor(exampleCredits / costs.standardImage)),
      deep: count(Math.floor(exampleCredits / costs.deepImage))
    }
  };
};

// Turns one locale's copy module into ready app-route content. FAQ lists may
// hold the 'creditUsage' placeholder, which becomes the locale's shared
// "how many credits does one image use" entry.
export const buildLocalizedOfferContent = (copy, language) => {
  const context = buildOfferContext(language);
  const creditUsageFaq = copy.creditUsageFaq(context);
  const build = (render) => {
    if (typeof render !== 'function') return null;
    const content = render(context);
    return {
      ...content,
      faq: content.faq.map((item) => (item === 'creditUsage' ? { ...creditUsageFaq } : item))
    };
  };

  return {
    pricing: build(copy.pricing),
    freeCredits: build(copy.freeCredits),
    creditUsageFaq: { ...creditUsageFaq },
    vectorizerAllowance: typeof copy.vectorizerAllowance === 'function'
      ? copy.vectorizerAllowance(context)
      : null
  };
};
